import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { useStudyFlow } from '../studyFlow';
import {
  createQbankBlockFromFilters,
  fetchQbankCounts,
  getQbankBlockById,
  getQbankLectureBlocks,
  normalizeQbankBlockResponse,
  type TopicTreeQbankStatusMode,
  type QbankCountSnapshot,
} from '../services/qbank';
import { updateBlock } from '../services/blocks';
import { startStandaloneQbankBlock } from '../helpers/qbankBlockHelpers';
import { Block, BlockType } from '../types/Study';
import { selectSelectedLectures } from '../slices/lectures/lecturesSlice';
import type { StudyReturnTo } from '../slices/studyContext/studyContextSlice';
import type { AppDispatch, RootState } from '../store/store';
import type { LecturePreview } from '../types/Lecture';
import { capture } from '../utils/analytics';
import { readApiErrorMessage } from '../services/apiError';

type LectureStudyStartOptions = {
  selectionNounPlural?: string;
  returnTo?: Exclude<StudyReturnTo, null>;
  returnPath?: string | null;
};

type QbankModalProps = {
  open: boolean;
  onClose: () => void;
  examName: string;
  startEventName?: string;
  defaultCount: number;
  qbankCounts?: QbankCountSnapshot;
  isSubmitting: boolean;
  isLoadingCounts: boolean;
  isViewAllSubmitting?: boolean;
  isViewAllDisabled?: boolean;
  onStart: (payload: {
    count: number;
    blockType: BlockType;
    qbankStatusMode: TopicTreeQbankStatusMode;
  }) => void;
  onViewAll?: () => void;
  onViewPreviousBlocks?: () => void;
  previousBlocks: Block[];
  isLoadingPreviousBlocks: boolean;
  hasFetchedPreviousBlocks: boolean;
  hasMorePreviousBlocks: boolean;
  expandPreviousBlocksAccordion: boolean;
  onLoadMorePreviousBlocks?: () => void;
  onReviewPreviousBlock?: (block: Block) => void;
};

export const useLectureQbankStudyStart = (
  selectedLecturesOverride?: LecturePreview[],
  options?: LectureStudyStartOptions
) => {
  const {
    selectionNounPlural = 'Lectures',
    returnTo = 'home',
    returnPath = null,
  } = options ?? {};

  const dispatch = useDispatch<AppDispatch>();
  const { sendPhaseEvent } = useStudyFlow();

  const selectedLecturesFromSlice = useSelector((state: RootState) =>
    selectSelectedLectures(state)
  );

  const selectedLectures =
    selectedLecturesOverride ?? selectedLecturesFromSlice;

  const [openStartQBankModal, setOpenStartQBankModal] = useState(false);
  const [isCreatingQbankBlock, setIsCreatingQbankBlock] = useState(false);

  const [lecturePreviousBlocks, setLecturePreviousBlocks] = useState<Block[]>(
    []
  );

  const [isLoadingLectureBlocks, setIsLoadingLectureBlocks] = useState(false);
  const [lectureBlocksPage, setLectureBlocksPage] = useState(1);
  const [hasMoreLectureBlocks, setHasMoreLectureBlocks] = useState(false);

  const [showLecturePreviousBlocks, setShowLecturePreviousBlocks] =
    useState(false);

  const [hasFetchedLectureBlocks, setHasFetchedLectureBlocks] = useState(false);
  const [qbankCounts, setQbankCounts] = useState<QbankCountSnapshot>();
  const [qbankCountsKey, setQbankCountsKey] = useState<string>();
  const [isLoadingQbankCounts, setIsLoadingQbankCounts] = useState(false);
  const isLoadingLectureBlocksRef = useRef(false);

  const lectureRefs = useMemo(
    () =>
      selectedLectures.map((lecture) => ({
        id: lecture.id,
        name: lecture.name,
      })),
    [selectedLectures]
  );

  const lectureIds = useMemo(
    () => lectureRefs.map((lecture) => lecture.id),
    [lectureRefs]
  );

  const lectureIdsKey = useMemo(
    () => [...lectureIds].sort().join(','),
    [lectureIds]
  );

  const lectureSelectionLabel = useMemo(() => {
    if (selectedLectures.length === 1) {
      return selectedLectures[0].name;
    }
    return `${selectedLectures.length} ${selectionNounPlural} Selected`;
  }, [selectedLectures, selectionNounPlural]);

  const aggregatedLectureCounts = useMemo<QbankCountSnapshot>(
    () =>
      selectedLectures.reduce(
        (acc, lecture) => ({
          total: acc.total + lecture.vignettes.total,
          new: acc.new + lecture.vignettes.new,
          due: acc.due + lecture.vignettes.due,
          scheduled: acc.scheduled + lecture.vignettes.scheduled,
          complete: acc.complete + lecture.vignettes.complete,
        }),
        { total: 0, new: 0, due: 0, scheduled: 0, complete: 0 }
      ),
    [selectedLectures]
  );

  // The deduped server counts are fetched once per request key. Keying on
  // the selected lectures' own counts (not just their ids) re-runs the fetch
  // when the rows are refreshed, e.g. once a mapping job finalizes: a document
  // selected while its job was still running had cached zero counts, which
  // kept the QBank tile hidden until the document was re-selected.
  const qbankCountsRequestKey = useMemo(
    () =>
      lectureIdsKey
        ? [
            lectureIdsKey,
            aggregatedLectureCounts.total,
            aggregatedLectureCounts.new,
            aggregatedLectureCounts.due,
            aggregatedLectureCounts.scheduled,
            aggregatedLectureCounts.complete,
          ].join('|')
        : '',
    [aggregatedLectureCounts, lectureIdsKey]
  );

  const hasResolvedQbankCountRequest = qbankCountsKey === qbankCountsRequestKey;

  const currentQbankCounts = hasResolvedQbankCountRequest
    ? qbankCounts
    : undefined;

  const resolvedQbankCounts = currentQbankCounts ?? aggregatedLectureCounts;

  const isResolvingQbankCounts =
    Boolean(lectureIdsKey) &&
    (isLoadingQbankCounts || !hasResolvedQbankCountRequest);

  const defaultQbankCount = useMemo(() => {
    const startableCount = Math.max(
      0,
      resolvedQbankCounts.new + resolvedQbankCounts.due
    );

    return startableCount > 0 ? Math.min(40, startableCount) : 0;
  }, [resolvedQbankCounts.due, resolvedQbankCounts.new]);

  useEffect(() => {
    setQbankCounts(undefined);
    setQbankCountsKey(undefined);

    if (!qbankCountsRequestKey) {
      setIsLoadingQbankCounts(false);
      return;
    }

    setIsLoadingQbankCounts(true);
    let isStale = false;

    const timeoutId = window.setTimeout(() => {
      const loadCounts = async () => {
        try {
          const response = await fetchQbankCounts({
            lectureIds: lectureIdsKey.split(','),
          });

          if (!isStale) {
            setQbankCounts(response.data);
            setQbankCountsKey(qbankCountsRequestKey);
          }
        } catch (error) {
          console.error('Failed to fetch lecture QBank counts:', error);
          if (!isStale) {
            setQbankCountsKey(qbankCountsRequestKey);
          }
        } finally {
          if (!isStale) {
            setIsLoadingQbankCounts(false);
          }
        }
      };

      void loadCounts();
    }, 150);

    return () => {
      isStale = true;
      window.clearTimeout(timeoutId);
    };
  }, [lectureIdsKey, qbankCountsRequestKey]);

  const preloadStandaloneBlock = useCallback(
    async (blockId: string, shouldReview: boolean) => {
      const response = await getQbankBlockById(blockId);
      const normalized = normalizeQbankBlockResponse(response);
      const block = normalized?.block;

      if (!block) {
        throw new Error('Failed to load lecture block.');
      }

      startStandaloneQbankBlock({
        dispatch,
        sendPhaseEvent,
        block,
        vignettes: normalized?.vignettes,
        shouldReview,
        returnTo,
        returnPath,
      });
    },
    [dispatch, sendPhaseEvent, returnTo, returnPath]
  );

  const resetLecturePreviousBlocks = useCallback(() => {
    setLecturePreviousBlocks([]);
    setIsLoadingLectureBlocks(false);
    isLoadingLectureBlocksRef.current = false;
    setLectureBlocksPage(1);
    setHasMoreLectureBlocks(false);
    setShowLecturePreviousBlocks(false);
    setHasFetchedLectureBlocks(false);
  }, []);

  const fetchLecturePreviousBlocks = useCallback(
    async (page: number, append = false, showResults = true) => {
      if (isLoadingLectureBlocksRef.current || lectureIds.length === 0) {
        return;
      }

      try {
        if (showResults) {
          setShowLecturePreviousBlocks(true);
        }
        isLoadingLectureBlocksRef.current = true;
        setIsLoadingLectureBlocks(true);

        const response = await getQbankLectureBlocks({
          lectureIds,
          page,
          limit: 10,
        });

        const { blocks } = response.data;
        const { meta } = response;

        setLecturePreviousBlocks((prev) =>
          append ? [...prev, ...blocks] : blocks
        );

        setHasMoreLectureBlocks(Boolean(meta?.hasMore));
        setLectureBlocksPage(page);
        setHasFetchedLectureBlocks(true);
      } catch (error) {
        console.error('Failed to fetch lecture blocks:', error);
        setHasFetchedLectureBlocks(true);
      } finally {
        isLoadingLectureBlocksRef.current = false;
        setIsLoadingLectureBlocks(false);
      }
    },
    [lectureIds]
  );

  useEffect(() => {
    if (!openStartQBankModal) {
      resetLecturePreviousBlocks();
      return;
    }

    resetLecturePreviousBlocks();
    if (lectureIds.length > 0) {
      void fetchLecturePreviousBlocks(1, false, false);
    }
  }, [
    fetchLecturePreviousBlocks,
    lectureIds,
    openStartQBankModal,
    resetLecturePreviousBlocks,
  ]);

  const handleLoadMoreLectureBlocks = useCallback(() => {
    if (isLoadingLectureBlocks || !hasMoreLectureBlocks) return;
    void fetchLecturePreviousBlocks(lectureBlocksPage + 1, true);
  }, [
    fetchLecturePreviousBlocks,
    hasMoreLectureBlocks,
    isLoadingLectureBlocks,
    lectureBlocksPage,
  ]);

  const handleStartQbankBlock = useCallback(
    async ({
      count,
      blockType,
      qbankStatusMode,
    }: {
      count: number;
      blockType: BlockType;
      qbankStatusMode: TopicTreeQbankStatusMode;
    }) => {
      const noVignettesMessage =
        'No QBank questions found for this lecture set.';

      if (lectureRefs.length === 0) {
        enqueueSnackbar('Please select at least one lecture first.', {
          variant: 'warning',
          autoHideDuration: 4000,
        });

        return;
      }

      try {
        setIsCreatingQbankBlock(true);

        const createdBlockResponse = await createQbankBlockFromFilters({
          block: {
            type: blockType,
            size: count,
          },
          filters: {
            contentType: 'qbank',
            count,
            lectures: lectureRefs,
            schemaVersion: 'v2',
            qbank: {
              statusMode: qbankStatusMode,
            },
          },
        });

        const normalizedBlock =
          normalizeQbankBlockResponse(createdBlockResponse);

        const createdBlock = normalizedBlock?.block;

        if (!createdBlock?.id) {
          throw new Error('Unable to start the created lecture block.');
        }

        const resolvedVignetteCount =
          createdBlock.vignetteCount ?? normalizedBlock?.vignettes?.length ?? 0;

        if (resolvedVignetteCount <= 0) {
          enqueueSnackbar(noVignettesMessage, {
            autoHideDuration: 5000,
          });

          return;
        }

        capture('lecture_qbank_block_create', {
          blockId: createdBlock.id,
          blockType,
          qbankCount: count,
          questionType: qbankStatusMode,
          lectureIds,
        });

        const startedBlock = { ...createdBlock, isStarted: true };

        startStandaloneQbankBlock({
          dispatch,
          sendPhaseEvent,
          block: startedBlock,
          vignettes: normalizedBlock?.vignettes,
          shouldReview: false,
          returnTo,
          returnPath,
        });

        void updateBlock({ blockId: createdBlock.id, isStarted: true });
        setOpenStartQBankModal(false);
      } catch (error: unknown) {
        const errorMessage = readApiErrorMessage(error);
        if (errorMessage?.toLowerCase().includes('no vignettes')) {
          enqueueSnackbar(noVignettesMessage, {
            variant: 'warning',
            autoHideDuration: 4000,
          });

          return;
        }

        enqueueSnackbar(
          errorMessage ??
            'Unable to start lecture QBank block. Please try again.',
          {
            variant: 'error',
            autoHideDuration: 5000,
          }
        );

        console.error('Failed to create lecture QBank block:', error);
      } finally {
        setIsCreatingQbankBlock(false);
      }
    },
    [dispatch, lectureIds, lectureRefs, sendPhaseEvent, returnTo, returnPath]
  );

  const handleViewAllQbankBlock = useCallback(async () => {
    const noVignettesMessage = 'No QBank questions found for this lecture set.';
    if (lectureRefs.length === 0) {
      enqueueSnackbar('Please select at least one lecture first.', {
        variant: 'warning',
        autoHideDuration: 4000,
      });

      return;
    }

    const viewAllCount = Math.max(0, resolvedQbankCounts.total);
    if (viewAllCount <= 0) {
      enqueueSnackbar(noVignettesMessage, {
        variant: 'warning',
        autoHideDuration: 4000,
      });

      return;
    }

    try {
      setIsCreatingQbankBlock(true);

      const createdBlockResponse = await createQbankBlockFromFilters({
        block: {
          type: BlockType.tutor,
          size: viewAllCount,
        },
        filters: {
          contentType: 'qbank',
          count: viewAllCount,
          lectures: lectureRefs,
          schemaVersion: 'v2',
          qbank: {
            statusMode: 'viewAll',
          },
        },
      });

      const normalizedBlock = normalizeQbankBlockResponse(createdBlockResponse);
      const createdBlock = normalizedBlock?.block;

      if (!createdBlock?.id) {
        throw new Error('Unable to start the created lecture block.');
      }

      const resolvedVignetteCount =
        createdBlock.vignetteCount ?? normalizedBlock?.vignettes?.length ?? 0;

      if (resolvedVignetteCount <= 0) {
        enqueueSnackbar(noVignettesMessage, {
          autoHideDuration: 5000,
        });

        return;
      }

      capture('lecture_qbank_block_create', {
        blockId: createdBlock.id,
        blockType: BlockType.tutor,
        qbankCount: viewAllCount,
        questionType: 'viewAll',
        lectureIds,
      });

      // "View All" is a review surface: every view the server sends is
      // read-only, and the answer route refuses writes into these blocks.
      const shouldReview = true;
      const startedBlock = { ...createdBlock, isStarted: true };

      startStandaloneQbankBlock({
        dispatch,
        sendPhaseEvent,
        block: startedBlock,
        vignettes: normalizedBlock?.vignettes,
        shouldReview,
        returnTo,
        returnPath,
      });

      void updateBlock({ blockId: createdBlock.id, isStarted: true });
      setOpenStartQBankModal(false);
    } catch (error: unknown) {
      const errorMessage = readApiErrorMessage(error);
      if (errorMessage?.toLowerCase().includes('no vignettes')) {
        enqueueSnackbar(noVignettesMessage, {
          variant: 'warning',
          autoHideDuration: 4000,
        });

        return;
      }

      enqueueSnackbar(
        errorMessage ??
          'Unable to start lecture QBank block. Please try again.',
        {
          variant: 'error',
          autoHideDuration: 5000,
        }
      );

      console.error('Failed to create lecture view all block:', error);
    } finally {
      setIsCreatingQbankBlock(false);
    }
  }, [
    dispatch,
    lectureIds,
    lectureRefs,
    resolvedQbankCounts.total,
    sendPhaseEvent,
    returnTo,
    returnPath,
  ]);

  const openQbankModalForLectures = useCallback(() => {
    if (lectureIds.length === 0) {
      enqueueSnackbar('Please select at least one lecture first.', {
        variant: 'warning',
        autoHideDuration: 4000,
      });

      return;
    }
    setOpenStartQBankModal(true);
  }, [lectureIds.length]);

  const qbankModalProps: QbankModalProps = useMemo(
    () => ({
      open: openStartQBankModal,
      onClose: () => setOpenStartQBankModal(false),
      examName: lectureSelectionLabel,
      startEventName: 'lecture_qbank_start',
      defaultCount: defaultQbankCount,
      qbankCounts: resolvedQbankCounts,
      isSubmitting: isCreatingQbankBlock,
      isLoadingCounts: isResolvingQbankCounts,
      isViewAllSubmitting: isCreatingQbankBlock || isResolvingQbankCounts,
      isViewAllDisabled: resolvedQbankCounts.total <= 0,
      onStart: handleStartQbankBlock,
      onViewAll: handleViewAllQbankBlock,
      onViewPreviousBlocks: () => {
        if (showLecturePreviousBlocks) {
          setShowLecturePreviousBlocks(false);
          return;
        }
        setShowLecturePreviousBlocks(true);
        if (!hasFetchedLectureBlocks && !isLoadingLectureBlocks) {
          void fetchLecturePreviousBlocks(1, false, true);
        }
      },
      previousBlocks: lecturePreviousBlocks,
      isLoadingPreviousBlocks: isLoadingLectureBlocks,
      hasFetchedPreviousBlocks: hasFetchedLectureBlocks,
      hasMorePreviousBlocks: hasMoreLectureBlocks,
      expandPreviousBlocksAccordion: showLecturePreviousBlocks,
      onLoadMorePreviousBlocks: handleLoadMoreLectureBlocks,
      onReviewPreviousBlock: (block) => {
        setOpenStartQBankModal(false);

        preloadStandaloneBlock(block.id, Boolean(block.isSubmitted)).catch(
          (error) => {
            console.error('Failed to resume lecture block:', error);
          }
        );
      },
    }),
    [
      defaultQbankCount,
      fetchLecturePreviousBlocks,
      handleLoadMoreLectureBlocks,
      handleStartQbankBlock,
      handleViewAllQbankBlock,
      hasFetchedLectureBlocks,
      hasMoreLectureBlocks,
      isCreatingQbankBlock,
      isLoadingLectureBlocks,
      isResolvingQbankCounts,
      lecturePreviousBlocks,
      lectureSelectionLabel,
      openStartQBankModal,
      preloadStandaloneBlock,
      resolvedQbankCounts,
      showLecturePreviousBlocks,
    ]
  );

  return {
    openQbankModalForLectures,
    qbankModalProps,
    qbankCounts: resolvedQbankCounts,
    isLoadingQbankCounts: isResolvingQbankCounts,
  };
};
